import React, { FC, useState } from 'react';

import Envelope from '@gravity-ui/icons/Envelope';
import { Button, Icon, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ApiError, sendEmail } from 'client/api';
import { installableAppLabels as l } from 'client/blocks/Settings/channels/labels';
import { useCurrentAliceApp } from 'client/hooks/useCurrentAliceApp';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as settingsLabelKeyset from 'i18n/SettingsLabels';

import { Field } from '../Field/Field';

import styles from './EmailField.module.scss';

interface EmailFieldProps {
    disabled: boolean;
    error?: string;
    onChange?: (value: string) => void;
    isVerified: boolean;
    touched?: boolean;
    value?: string;
}

const EMAIL_TOAST = 'EMAIL_TOAST';

const t = i18n(settingsLabelKeyset);
const tGeneralSettings = i18n(generalSettingsKeyset);

const fieldDescription = (isEmailSent: boolean) => {
    if (isEmailSent) return t('description-email');

    return (
        <span>
            {t('description-email')}
            <br />
            {t('description-email-sent')}
        </span>
    );
};

export const EmailField: FC<EmailFieldProps> = ({ disabled, error, isVerified, onChange, touched, value }) => {
    const [isLoading, setIsLoading] = useState(false);

    const installableApp = useCurrentAliceApp()!;
    const { add: addToast } = useToaster();
    const isEmailSent = !!value && !isVerified && !touched;

    const handleSend = async () => {
        if (!value) return;
        setIsLoading(true);
        try {
            await sendEmail({
                userId: __appConfig.user.blackbox.uid!,
                skillId: installableApp.id,
                email: value,
            });
            addToast({ title: tGeneralSettings('sent'), theme: 'success', name: EMAIL_TOAST });
        } catch (e) {
            if (e instanceof ApiError && e.code === 429) {
                addToast({ title: tGeneralSettings('try-again'), theme: 'danger', name: EMAIL_TOAST });
            } else {
                addToast({ title: tGeneralSettings('error'), theme: 'danger', name: EMAIL_TOAST });
            }
        }
        setIsLoading(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!onChange) return;
        onChange(e.target.value);
    };

    return (
        <Field fieldTitle={l.email} fieldDescription={fieldDescription(isEmailSent)}>
            <TextInput
                className={styles.container}
                disabled={disabled}
                error={error}
                onChange={handleChange}
                size="xl"
                type="email"
                value={value}
            />
            {isEmailSent ? (
                <Button className={styles.button} disabled={disabled} onClick={handleSend} loading={isLoading}>
                    <Icon data={Envelope} />
                    {t('send-again')}
                </Button>
            ) : null}
        </Field>
    );
};
